import React, { useState, useEffect, useCallback } from "react";
import "./adminReferrals.css";
import { useNavigate } from "react-router-dom";
import AdminTabs from "./AdminTabs";
import { isSuperAdmin } from "../../utils/roles";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faBan, faMoneyBillWave, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

const AdminReferrals = ({ isLoggedIn, loginUser, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleError = useCallback((err) => {
    if (err.response && err.response.status === 401) {
      onLogout();
      navigate("/login");
    } else if (err.response) {
      setError(err.response.data.message || "⚠️ Something went wrong.");
    } else {
      setError("⚠️ An error occurred while loading the referral panel.");
    }
  }, [onLogout, navigate]);

  const fetchOverview = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/referral/admin/overview");
      setData(response.data);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const reviewReferral = async (referralId, action) => {
    if (busy) return; // guard against double-clicks while a request is in flight
    try {
      setBusy(true);
      await axios.post(`/api/v1/referral/admin/review/${referralId}?_method=PUT`, { action });
      await fetchOverview();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (rewardId) => {
    if (busy) return; // guard against double-clicks while a request is in flight
    try {
      setBusy(true);
      await axios.post(`/api/v1/referral/admin/payout/${rewardId}?_method=PUT`);
      await fetchOverview();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!isLoggedIn || !isSuperAdmin(loginUser)) {
    navigate("/");
    return null;
  }

  if (loading) {
    return (
      <div className="admin-ref">
        <div className="admin-ref__top">
          <div className="admin-ref__top-content">
            <h1 className="admin-ref__title">Referral Admin</h1>
          </div>
        </div>
        <div className="admin-ref__body">
          <AdminTabs loginUser={loginUser} />
          <p className="admin-ref__empty">Loading referral panel...</p>
        </div>
      </div>
    );
  }

  const stats = data ? data.stats : {};

  return (
    <div className="admin-ref">
      <div className="admin-ref__top">
        <div className="admin-ref__top-content">
          <h1 className="admin-ref__title">Referral Admin</h1>
        </div>
      </div>

      <div className="admin-ref__body">

      <AdminTabs loginUser={loginUser} />

      {error !== "" && <div className="admin-ref__error">{error}</div>}

      {/* Stats */}
      <div className="admin-ref__stats">
        <div className="admin-ref__stat"><span>{stats.totalReferrals || 0}</span><label>Total referrals</label></div>
        <div className="admin-ref__stat"><span>{stats.qualifiedCount || 0}</span><label>Qualified</label></div>
        <div className="admin-ref__stat"><span>{stats.rewardedCount || 0}</span><label>Rewarded</label></div>
        <div className="admin-ref__stat"><span>{stats.flaggedCount || 0}</span><label>Flagged</label></div>
        <div className="admin-ref__stat"><span>Rs. {stats.totalRewardAmount || 0}</span><label>Total rewards</label></div>
        <div className="admin-ref__stat highlight"><span>Rs. {stats.unpaidAmount || 0}</span><label>Unpaid ({stats.unpaidRewards || 0})</label></div>
      </div>

      {/* Pending payouts */}
      <div className="admin-ref__section">
        <h2><FontAwesomeIcon icon={faMoneyBillWave} /> Pending payouts</h2>
        {(!data || data.pendingPayouts.length === 0) ? (
          <p className="admin-ref__empty">No payouts pending. 🎉</p>
        ) : (
          data.pendingPayouts.map((reward) => (
            <div key={reward._id} className="admin-ref__row">
              <div>
                <p className="admin-ref__name">{reward.referrer ? reward.referrer.userName : "Unknown"}</p>
                <p className="admin-ref__meta">
                  Code: {reward.referrer ? reward.referrer.referralCode : "-"} ·
                  {reward.referrer ? ` ${reward.referrer.phoneNumber || reward.referrer.email || ""}` : ""}
                </p>
              </div>
              <div className="admin-ref__row-actions">
                <span className="admin-ref__amount">Rs. {reward.amount}</span>
                <button className="admin-ref__pay-btn" onClick={() => markPaid(reward._id)} disabled={busy}>
                  <FontAwesomeIcon icon={faCircleCheck} /> Mark paid
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Flagged referrals */}
      <div className="admin-ref__section">
        <h2><FontAwesomeIcon icon={faTriangleExclamation} /> Flagged referrals (possible spam)</h2>
        {(!data || data.flagged.length === 0) ? (
          <p className="admin-ref__empty">Nothing flagged. All clear.</p>
        ) : (
          data.flagged.map((ref) => (
            <div key={ref._id} className="admin-ref__row">
              <div>
                <p className="admin-ref__name">
                  {ref.referredUser ? ref.referredUser.userName : "Deleted user"}
                  <span className="admin-ref__meta"> referred by {ref.referrer ? ref.referrer.userName : "?"}</span>
                </p>
                <p className="admin-ref__reasons">
                  {(ref.spamReasons || []).map((r) => (
                    <span key={r} className="admin-ref__tag">{r.replace(/_/g, " ")}</span>
                  ))}
                </p>
                <p className="admin-ref__meta">IP: {ref.signupIp || "n/a"} · {ref.signupEmail || ""}</p>
              </div>
              <div className="admin-ref__row-actions">
                <button className="admin-ref__approve-btn" onClick={() => reviewReferral(ref._id, "approve")} disabled={busy}>
                  <FontAwesomeIcon icon={faCircleCheck} /> Approve
                </button>
                <button className="admin-ref__reject-btn" onClick={() => reviewReferral(ref._id, "reject")} disabled={busy}>
                  <FontAwesomeIcon icon={faBan} /> Reject
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Top referrers */}
      <div className="admin-ref__section">
        <h2>Top referrers</h2>
        {(!data || data.topReferrers.length === 0) ? (
          <p className="admin-ref__empty">No successful referrals yet.</p>
        ) : (
          data.topReferrers.map((r) => (
            <div key={r._id} className="admin-ref__row">
              <div>
                <p className="admin-ref__name">{r.userName}</p>
                <p className="admin-ref__meta">Code: {r.referralCode || "-"}</p>
              </div>
              <div className="admin-ref__row-actions">
                <span className="admin-ref__amount">{r.successful} signups</span>
                <span className="admin-ref__meta">Rs. {r.rewardBalance || 0} unpaid</span>
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
};

export default AdminReferrals;
